import type { CustomerOverview } from '@vy/api-definition';
import { VySelectField } from '../../../../../components/design/inputs';
import { CUSTOMER_STATUSES, CustomerStatus } from '@vy/types';
import { Box, Stack, Typography } from '@mui/material';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';
import { STATUS_COLORS_CONFIG } from '../../../../customers/CustomersPage/customers-page-components/CustomerStatusSelect/constants/statusColorsConfig';
import { StatusLabel } from '../../../../../components/new-design/inputs/VyStatusSelect/StatusLabel';
import { getStatusDisplayLabel } from '../../../../customers/CustomersPage/utils/getStatusDisplayLabel';
import { createContractFormSelectors, getSelectorAttribute } from '@vy/app-selectors';

type CustomerSelectionSectionProps = {
  customers: CustomerOverview[];
};

const NON_SELECTABLE_CUSTOMER_STATUSES = new Set<CustomerStatus>([
  CUSTOMER_STATUSES.Suspended,
  CUSTOMER_STATUSES.Inactive,
]);

export const CustomerSelectionSection: React.FC<CustomerSelectionSectionProps> = ({ customers }) => {
  return (
    <div {...getSelectorAttribute(createContractFormSelectors.customerSelect)}>
      <VySelectField
        name="customerId"
        options={customers}
        label="Search for a customer"
        getOptionLabel={(option) => option.name as string}
      renderOption={(props, option) => {
        const isDisabled = NON_SELECTABLE_CUSTOMER_STATUSES.has(option.status);
        const { backgroundColor, color } = STATUS_COLORS_CONFIG[option.status as CustomerStatus];

        const menuItem = (
          <Box
            component="li"
            {...props}
            aria-disabled={isDisabled}
            sx={isDisabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            key={option.id}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%"
              }}>
              <Typography>{option.name}</Typography>
              {NON_SELECTABLE_CUSTOMER_STATUSES.has(option.status) && (
                <StatusLabel
                  label={getStatusDisplayLabel(option.status)}
                  backgroundColor={backgroundColor}
                  color={color}
                  sx={{
                    width: '100px !important',
                  }}
                />
              )}
            </Stack>
          </Box>
        );

        return isDisabled ? (
                    <VyTooltip
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(13, 27, 39, 0.87)',
                  color: '#FFFFFF',
                },
              },
            }}
            key={option.id}
            tooltipContent="In order to create a contract for this customer, change its status to active"
            placement="right-end"
          >
            <Box>{menuItem}</Box>
          </VyTooltip>
        ) : (
          menuItem
        );
      }}
      placeholder="Search for a customer"
      focused
    />
    </div>
  );
};
